import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { resetOnLogout } from '~/reducers/common-actions';
import type { RootState } from '~/store';
import type {
    DriversByClientState,
    AddDriverByClientPayload,
    UpdateDriversByClientPayload,
    RemoveDriverByIdPayload
} from './types';
import { defaultDriversByClientState } from './types';
import { getAllDriversFromState, isValidPayloadData } from './utils';
import type { ApiDriver } from '~/api/types';

export const driversByClientSlice = createSlice({
    name: 'driversByClient',
    initialState: defaultDriversByClientState,
    reducers: {
        setDriversByClient: (
            state: DriversByClientState,
            action: PayloadAction<DriversByClientState>
        ) => {
            return action.payload;
        },
        addDriverByClient: (
            state: DriversByClientState,
            action: PayloadAction<AddDriverByClientPayload>
        ) => {
            const { data, clientId } = action.payload;
            const isValidData = isValidPayloadData(data, clientId);
            if (!isValidData) return state;

            const driversList = state[clientId];
            const newDriversList = driversList
                ? [...driversList, data]
                : [data];

            return {
                ...state,
                [clientId]: newDriversList
            };
        },
        updateDriversByClient: (
            state: DriversByClientState,
            action: PayloadAction<UpdateDriversByClientPayload>
        ) => {
            const { driverId, data, clientId } = action.payload;
            const isValidData = isValidPayloadData(data, clientId);
            const hasClient = Boolean(state[clientId]);

            if (!isValidData || !hasClient) return state;

            const updatedDrivers = state[clientId].map((driver) => {
                if (driver.id === driverId) return data;
                return driver;
            });
            return {
                ...state,
                [clientId]: updatedDrivers
            };
        },
        resetDriversByClient: () => {
            return defaultDriversByClientState;
        },
        removeDriverById: (
            state: DriversByClientState,
            action: PayloadAction<RemoveDriverByIdPayload>
        ) => {
            const { id: driverId, clientId } = action.payload;
            const hasClient = Boolean(state[clientId]);

            if (!hasClient) return state;

            const updatedDrivers = state[clientId].filter((driver) => {
                return driver.id !== driverId;
            });
            return {
                ...state,
                [clientId]: updatedDrivers
            };
        }
    },
    extraReducers: (builder) => {
        builder.addCase(resetOnLogout, () => {
            return defaultDriversByClientState;
        });
    }
});

export const {
    setDriversByClient,
    addDriverByClient,
    updateDriversByClient,
    resetDriversByClient,
    removeDriverById
} = driversByClientSlice.actions;

export const selectDriversByClient = (state: RootState) =>
    state.driversByClient;

export const selectDriverById = (driverId: string) => (state: RootState) => {
    const allDrivers = getAllDriversFromState(state.driversByClient);
    return allDrivers.find((driver) => driver.id === driverId);
};

/**
 * Retrieves a mapping of drivers, keyed to the driver ID
 */
export const selectDrivers = (state: RootState) => {
    const allDrivers = getAllDriversFromState(state.driversByClient);

    return allDrivers.reduce((acc, driver) => {
        const { id: driverId } = driver;
        if (driverId) acc[driverId] = driver;
        return acc;
    }, {} as Record<string, ApiDriver>);
};

/**
 * Retrieves a mapping of drivers with a default vehicle, keyed to the vehicle ID
 */
export const selectDriverVehiclePairs = (state: RootState) => {
    const allDrivers = getAllDriversFromState(state.driversByClient);

    return allDrivers.reduce((acc, driver) => {
        const { defaultVehicle: vehicleId } = driver;
        if (vehicleId) acc[vehicleId] = driver;
        return acc;
    }, {} as Record<string, ApiDriver>);
};

export default driversByClientSlice.reducer;
